import { ReactNode, useMemo } from 'react';
import {
    ButtonBase,
    paletteColors,
    Popover,
    SxProps,
    Theme,
    Box,
    Breakpoint,
} from '@evaneos/b2b-ui';

import { useEditPopover } from './hooks';

export type EditableTriggerProps = {
    labelId: string;
    baseId?: string;
    children: ReactNode;
    /**
     * Render function for the popover content (the editing interface).
     * Receives a `close` function to programmatically close the popover.
     *
     * @example
     * editor={({ close }) => (
     *   <Box p={2}>
     *     <TextField />
     *     <Button onClick={close}>Save</Button>
     *   </Box>
     * )}
     */
    editor: (ctx: { close: () => void }) => ReactNode;
    /**
     * Width of the popover. Can be:
     * - 'trigger': Match trigger width exactly
     * - 'fit-content': Match exactly the content of the popover
     * - number: Fixed pixel width
     * - undefined: Default (min: 280px, max: 450px)
     * - responsive object: { xs: 'trigger', sm: 400, md: 600 }
     */
    popoverWidth?:
        | 'trigger'
        | 'fit-content'
        | number
        | Partial<Record<Breakpoint, 'trigger' | number>>;
    /**
     * Popover placement relative to anchor:
     * - 'below': Opens below anchor (default)
     * - 'over': Opens over anchor
     */
    placement?: 'below' | 'over';
    /**
     * Called before the popover closes. Return false to prevent closing.
     * Useful for validation and saving data.
     */
    onBeforeClose?: () => boolean | void;
    /**
     * Disable the automatic popover minHeight based on trigger height.
     * By default, the popover's minHeight matches the trigger's height.
     * Set to true to remove this constraint (useful for dynamic content that can shrink).
     */
    disablePopoverMinHeight?: boolean;
    /**
     * Optional hint to display on hover/focus
     */
    hoverHint?: ReactNode;
    /**
     * Optional action to display on hover (rendered as sibling to avoid nested buttons)
     */
    hoverAction?: ReactNode;
    /**
     * Always show hover action on touch devices (where hover doesn't work)
     */
    showHoverActionOnTouch?: boolean;
    /**
     * Optional disable trigger button
     */
    disabled?: boolean;

    /**
     * Custom styles for the trigger button (merged with defaults)
     */
    triggerSx?: SxProps<Theme>;
    /**
     * Custom anchor element for the popover.
     * If not provided, uses the trigger button as anchor.
     * Used by EditableGroup to anchor all popovers to the container.
     */
    anchorEl?: HTMLElement | null | (() => HTMLElement | null);
};

/**
 * Internal component that combines a trigger button with an edit popover.
 * Used by both EditableValue and EditableGroup.
 */
export function EditableTrigger({
    labelId,
    baseId,
    children,
    editor,
    popoverWidth,
    placement = 'below',
    triggerSx,
    anchorEl: customAnchorEl,
    onBeforeClose,
    disablePopoverMinHeight = false,
    hoverHint,
    hoverAction,
    showHoverActionOnTouch = false,
    disabled = false,
}: EditableTriggerProps) {
    const { getTriggerProps, getPopoverProps, close, triggerWidth, triggerHeight, anchorEl, open } =
        useEditPopover(labelId, baseId, onBeforeClose);

    const actualAnchorEl =
        (typeof customAnchorEl === 'function' ? customAnchorEl() : customAnchorEl) || anchorEl;

    // Measure dimensions from the actual anchor element when open
    const measureWidth =
        open && actualAnchorEl ? actualAnchorEl.getBoundingClientRect().width : triggerWidth;
    const measureHeight =
        open && actualAnchorEl ? actualAnchorEl.getBoundingClientRect().height : triggerHeight;

    const placementConfig =
        placement === 'over'
            ? {
                  anchorOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
                  transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
              }
            : {
                  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
                  transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
              };

    const widthStyles = useMemo(() => {
        // Handle responsive width object
        if (typeof popoverWidth === 'object' && popoverWidth !== null) {
            const responsiveWidths: Record<string, number> = {};

            Object.entries(popoverWidth).forEach(([breakpoint, value]) => {
                responsiveWidths[breakpoint] =
                    value === 'trigger' ? Math.max(measureWidth || 280, 280) : value;
            });

            return { width: responsiveWidths };
        }

        // Handle simple width value
        if (!popoverWidth) return { minWidth: 280, maxWidth: 450 };
        if (popoverWidth === 'trigger') return { width: measureWidth || 280 };
        return { width: popoverWidth };
    }, [popoverWidth, measureWidth]);

    const defaultTriggerSx: SxProps<Theme> = {
        display: 'block',
        textAlign: 'left',
        borderRadius: 1,
        '&:focus-visible': { bgcolor: paletteColors.neutral[200] },
    };

    const wrapperSx: SxProps<Theme> | undefined = hoverAction
        ? {
              position: 'relative',
              '&:hover [data-hover-action], &:focus-within [data-hover-action]': {
                  opacity: 1,
              },
              '&:hover [data-trigger-button]': {
                  bgcolor: paletteColors.neutral[100],
              },
              ...(showHoverActionOnTouch && {
                  '@media (hover: none)': {
                      '[data-hover-action]': {
                          opacity: 1,
                      },
                  },
              }),
          }
        : undefined;

    const triggerButton = (
        <ButtonBase
            data-trigger-button
            disableRipple
            disableTouchRipple
            disabled={disabled}
            {...getTriggerProps({
                sx: {
                    ...defaultTriggerSx,
                    ...(triggerSx ?? {}),
                    ...(hoverHint && {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '& [data-hint-content]': {
                            opacity: 0,
                            transition: 'opacity 0.2s ease-out',
                        },
                        '&:hover [data-hint-content], &:focus-visible [data-hint-content]': {
                            opacity: 1,
                        },
                    }),
                },
            })}
        >
            {children}
            {hoverHint && (
                <Box component="span" sx={{ marginLeft: 'auto' }} data-hint-content>
                    {hoverHint}
                </Box>
            )}
        </ButtonBase>
    );

    return (
        <>
            {hoverAction ? (
                <Box sx={wrapperSx}>
                    {triggerButton}
                    <Box
                        data-hover-action
                        sx={{
                            position: 'absolute',
                            top: 2,
                            right: 4,
                            opacity: 0,
                            zIndex: 2,
                            transition: 'opacity 0.2s ease-out',
                        }}
                    >
                        {hoverAction}
                    </Box>
                </Box>
            ) : (
                triggerButton
            )}

            <Popover
                {...getPopoverProps(
                    {
                        anchorEl: actualAnchorEl,
                        slotProps: {
                            paper: {
                                role: 'dialog',
                                'aria-labelledby': labelId,
                                sx: {
                                    ...widthStyles,
                                    minHeight: disablePopoverMinHeight
                                        ? undefined
                                        : measureHeight || 32,
                                    boxShadow: 2,
                                    overflow: 'visible',
                                },
                            },
                        },
                    },
                    placementConfig,
                )}
            >
                {editor({ close })}
            </Popover>
        </>
    );
}
